import { createContext, FC, ReactNode } from 'react';
import { pluralize, titleize } from 'inflection';
import { AllFilters, AllFiltersDrawer, BulkActions, ColumnVisibility, ColumnVisibilityDrawer, Export, FilterSummary, QuickFilters, SavedConfigErrorBoundary, Search, TableHead, TableBody, TablePagination } from './';
import { CacheKeyGenerator, Column, ResultsPromiseFromCacheKey, TableName, useTable } from 'hooks/useTable';
import styles from './CbTable.module.css';
interface TableProps {
  additionalFilters?: any[];
  bulkActions?: ReactNode[];
  columnDefs: any[];
  configOverride?: any;
  defaultConfig: any;
  drawers?: ReactNode[];
  exportModal?: FC<any>;
  extraHeaderContent?: ReactNode;
  getCacheKey: CacheKeyGenerator;
  getCSVData?: (config: any, columnDefs: any) => Promise<any>;
  getResults: ResultsPromiseFromCacheKey;
  getSelectedRowValue?: (row) => any;
  hasBulkSelect?: boolean;
  label: string;
  description?: string;
  primaryActions?: ReactNode[];
  tableName: TableName;
  hideExport?: boolean;
  emptyState?: ReactNode;
}
export type TableContextType = ReturnType<typeof useTable> & {
  columnDefs: Column[];
};

/* Casting here is kind of lying, but it removes the cast from all the consumer sites */
export const TableContext = createContext({} as TableContextType);
export const TableContextConsumer = TableContext.Consumer;
export const CbTable: FC<TableProps> = ({
  additionalFilters = [],
  bulkActions = [],
  columnDefs,
  configOverride = {},
  defaultConfig,
  drawers = [],
  exportModal,
  extraHeaderContent = null,
  getCacheKey,
  getCSVData,
  getResults,
  getSelectedRowValue,
  hasBulkSelect = false,
  label,
  description = '',
  primaryActions = [],
  tableName,
  hideExport = false,
  emptyState = null
}) => {
  const hookReturn = useTable({
    additionalFilters,
    columnDefs,
    configOverride,
    defaultConfig,
    exportModal,
    getCacheKey,
    getCSVData,
    getResults,
    getSelectedRowValue,
    hasBulkSelect,
    label,
    tableName,
    hideExport,
    emptyState
  });
  const {
    selectedRows
  } = hookReturn;
  const numberOfRowsSelected = Object.values(selectedRows).filter(value => value).length;
  return <TableContext.Provider value={{
    ...hookReturn,
    columnDefs
  }} data-sentry-element="unknown" data-sentry-component="CbTable" data-sentry-source-file="CbTable.tsx">
      <SavedConfigErrorBoundary data-sentry-element="SavedConfigErrorBoundary" data-sentry-source-file="CbTable.tsx">
        <div>
          <div className={styles.titleBar}>
            <div className={styles.titleContainer}>
              <span className={styles.title}>{titleize(pluralize(label))}</span>
              {description && <span className={styles.subtitle}>{description}</span>}
            </div>
            <div>
              <Search data-sentry-element="Search" data-sentry-source-file="CbTable.tsx" />
              {primaryActions.map((action, index) => <div key={index} className={styles.action}>
                  {action}
                </div>)}
            </div>
          </div>
          {extraHeaderContent}
          <div className={styles.controlBar}>
            <div className={styles.leftControlContainer}>
              <QuickFilters data-sentry-element="QuickFilters" data-sentry-source-file="CbTable.tsx" />
              <FilterSummary data-sentry-element="FilterSummary" data-sentry-source-file="CbTable.tsx" />
            </div>
            <div className={styles.rightControlContainer}>
              {numberOfRowsSelected > 1 && hasBulkSelect && <BulkActions bulkActions={bulkActions} />}
              <AllFilters data-sentry-element="AllFilters" data-sentry-source-file="CbTable.tsx" />
              <ColumnVisibility data-sentry-element="ColumnVisibility" data-sentry-source-file="CbTable.tsx" />
              <Export data-sentry-element="Export" data-sentry-source-file="CbTable.tsx" />
            </div>
          </div>
          <div className={styles.tableContainerWithPagination}>
            <div className={styles.tableContainer}>
              <table className={styles.baseTable}>
                <TableHead data-sentry-element="TableHead" data-sentry-source-file="CbTable.tsx" />
                <TableBody data-sentry-element="TableBody" data-sentry-source-file="CbTable.tsx" />
              </table>
            </div>
            <TablePagination data-sentry-element="TablePagination" data-sentry-source-file="CbTable.tsx" />
          </div>
        </div>
        {drawers}
        <AllFiltersDrawer data-sentry-element="AllFiltersDrawer" data-sentry-source-file="CbTable.tsx" />
        <ColumnVisibilityDrawer data-sentry-element="ColumnVisibilityDrawer" data-sentry-source-file="CbTable.tsx" />
      </SavedConfigErrorBoundary>
    </TableContext.Provider>;
};